/* global L */
import React, { useEffect, useRef, useState } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useTranslation } from 'react-i18next';
import { getPartners, getFileUrl } from '../services/apiService';
import './Partners.css';
import partenaireImg from '../aseets/images/partenaire/partenaire.jpg';

const Partners = () => {
  const { t } = useTranslation();
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Work Package metadata for fallback display
  const wpMeta = {
    WP0: { title: 'Project Coordination and Management', lead: 'ERU (Türkiye)', personMonths: '10', startMonth: '1' },
    WP1: { title: 'Desk Analysis and Living Labs (LL)', lead: 'ERU (Türkiye)', personMonths: '22.2', startMonth: '1' },
    WP2: { title: 'Developing a Digital Toolbox for Urban Agriculture', lead: 'JASSP SAS, URCA (France)', personMonths: '30', startMonth: '1' },
    WP3: { title: 'Reusing Irrigation Water and Effective Irrigation', lead: 'ERU (Türkiye)', personMonths: '50', startMonth: '1' },
    WP4: { title: 'Composting from Agricultural and Urban Waste', lead: 'USMS (Morocco)', personMonths: '30', startMonth: '6' },
    WP5: { title: 'Extraction of High-Value Plant Proteins from Agricultural Waste', lead: 'METU (Türkiye)', personMonths: '24.8', startMonth: '6' },
    WP6: { title: 'Extraction of Economically Significant Food Additives from Agricultural Waste', lead: 'TUBITAK MRC (Türkiye)', personMonths: '7.2', startMonth: '6' },
    WP7: { title: 'Policy Recommendations for Urban Agriculture', lead: 'ERU (Türkiye)', personMonths: '7', startMonth: '12' },
    WP8: { title: 'Dissemination, Exploitation, Communication, and Outreach', lead: 'ERU (Türkiye)', personMonths: '22.2', startMonth: '1' }
  };


  useEffect(() => {
    const fetchPartners = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await getPartners();
        setPartners(data || []);
      } catch (err) {
        console.error('Error fetching partners:', err);
        setError('Error loading partners');
        setPartners([]);
      } finally {
        setLoading(false);
      }
    };

    fetchPartners();
  }, []);
  
  const PartnersMap = () => {
    const { t: tMap } = useTranslation();
    const mapRef = useRef(null);
    const mapInstance = useRef(null);
    const [viewportWidth, setViewportWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);
    const [selectedPartner, setSelectedPartner] = useState(null);
    const isMobile = viewportWidth <= 768;

    useEffect(() => {
      const handleResize = () => setViewportWidth(window.innerWidth);
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Transformer les partenaires de la base en format pour la carte
    // Utiliser directement les données de la base de données
    const partnersMapData = partners.map(p => {
      const isCoordinator = p.isCoordinator || false;
      const logo = p.logoUrl ? getFileUrl(p.logoUrl) : (p.logo || '🏢');
      const logoColor = p.logoColor || '#96ca2d';
      const coordinates = p.coordinates || [40, 10]; // Fallback: centre Méditerranée
      const color = p.color || '#96ca2d';
      
      return {
        id: p.id || p.mongoId,
        name: p.name,
        fullName: p.fullName || p.name,
        country: p.country,
        logo: logo,
        logoUrl: p.logoUrl ? getFileUrl(p.logoUrl) : null,
        logoColor: logoColor,
        color: color,
        coordinates: coordinates,
        pi: p.pi || '',
        website: p.website || null,
        isCoordinator: isCoordinator,
        workPackagesLead: p.workPackagesLead || [],
        workPackagesContributing: p.workPackagesContributing || [],
        expertise: p.expertise || ''
      };
    });

    const createIcon = (p, selected = false) => {
      return L.divIcon({
        html: `
          <div style="
            width: ${selected ? '80px' : '60px'};
            height: ${selected ? '80px' : '60px'};
            border-radius: 50%;
            background: linear-gradient(135deg, ${p.color}, ${p.color}dd);
            display: flex;
            align-items: center;
            justify-content: center;
            color: white;
            border: 4px solid white;
            box-shadow: ${selected ? '0 15px 40px rgba(0,0,0,0.4), 0 0 30px ' + p.color + '80' : '0 8px 25px rgba(0,0,0,0.3)'};
            transform: ${selected ? 'scale(1.2)' : 'scale(1)'};
            transition: all 0.3s ease;
            position: relative;
            overflow: visible;
            ${selected ? 'animation: pulse 2s infinite;' : ''}
          ">
            <div style="
              width: 100%;
              height: 100%;
              border-radius: 50%;
              overflow: hidden;
              display: flex;
              align-items: center;
              justify-content: center;
              font-size: ${selected ? '0.9rem' : '0.75rem'};
              font-weight: 700;
              text-align: center;
              padding: 4px;
              word-wrap: break-word;
              line-height: 1.1;
            ">
              ${p.name}
            </div>
            ${p.isCoordinator ? `<div style="position:absolute; top:-8px; right:-8px; background:#ffd700; color:#2c3e50; font-weight:700; font-size:.65rem; padding:3px 7px; border-radius:12px; z-index:1000; box-shadow:0 2px 8px rgba(0,0,0,0.3); white-space:nowrap; border:2px solid #fff;">${tMap('partners.map.coordShort')}</div>` : ''}
          </div>
        `,
        className: 'partner-icon',
        iconSize: [selected ? 80 : 60, selected ? 80 : 60],
        iconAnchor: [selected ? 40 : 30, selected ? 40 : 30],
      });
    };

    useEffect(() => {
      if (!mapRef.current || partners.length === 0) return;
      // Ensure previous instance is fully destroyed
      if (mapInstance.current) {
        mapInstance.current.remove();
        mapInstance.current = null;
      }
      if (mapRef.current._leaflet_id) {
        try { delete mapRef.current._leaflet_id; } catch (e) { /* ignore */ }
      }

      mapInstance.current = L.map(mapRef.current, {
        center: [40, 10],
        zoom: 4,
        // Align with Home.js behavior: disable interactions on mobile
        zoomControl: !isMobile,
        scrollWheelZoom: !isMobile,
        doubleClickZoom: !isMobile,
        boxZoom: !isMobile,
        keyboard: !isMobile,
        dragging: !isMobile,
        touchZoom: !isMobile,
        tap: !isMobile,
      });

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap contributors',
        maxZoom: 18,
        minZoom: 3,
      }).addTo(mapInstance.current);

      const allBounds = L.latLngBounds();

      partnersMapData.forEach((p) => {
        // Utiliser les work packages depuis la base de données
        const leadWPs = p.workPackagesLead || [];
        const contribWPs = p.workPackagesContributing || [];
        
        const leadCodesStr = leadWPs.map(wp => typeof wp === 'string' ? wp : wp.id).join(', ');
        const contribCodesStr = contribWPs.map(wp => typeof wp === 'string' ? wp : wp.id).join(', ');
        const marker = L.marker(p.coordinates, { icon: createIcon(p) }).addTo(mapInstance.current);
        
        // Logo pour le popup - utiliser logoUrl si disponible, sinon emoji
        const logoHtml = p.logoUrl 
          ? `<img src="${p.logoUrl}" alt="${p.name}" style="width:48px;height:48px;object-fit:contain;border-radius:50%;border:2px solid ${p.color};" onerror="this.style.display='none'; this.parentElement.innerHTML='<div style=\\'width:48px;height:48px;display:flex;align-items:center;justify-content:center;font-size:1.6rem;background:linear-gradient(135deg,${p.color},${p.color}dd);border-radius:50%;\\'>${p.logo || '🏢'}</div>';" />`
          : `<div style="font-size:1.6rem;">${p.logo || '🏢'}</div>`;
        
        const popup = `
          <div style="max-width:320px; font-family: Arial, sans-serif;">
            <div style="display:flex; align-items:center; gap:10px; margin-bottom:6px;">
              ${logoHtml}
              <div>
                <div style="font-weight:700; color:${p.color}; line-height:1.2;">${p.name} <span style="color:#2c3e50; font-weight:600">${p.fullName}</span></div>
                <div style="font-size:.9rem; color:#666">${p.country}${p.isCoordinator ? ` · <strong style="color:#2c3e50">${tMap('partners.map.coordinator')}</strong>` : ''}</div>
                ${p.pi ? `<div style="font-size:.85rem; color:#888; font-style:italic; margin-top:2px;">${tMap('home.consortium.principalInvestigator', 'PI')}: ${p.pi}</div>` : ''}
              </div>
            </div>
            ${leadCodesStr ? `<div style=\"font-size:.9rem; color:#2c3e50; margin:4px 0;\"><strong>${tMap('home.consortium.leadLabel')}</strong> ${leadCodesStr}</div>` : ''}
            ${contribCodesStr ? `<div style=\"font-size:.9rem; color:#6c757d; margin:0 0 6px 0;\"><strong>${tMap('home.consortium.contributingLabel')}</strong> ${contribCodesStr}</div>` : ''}
            ${p.website && p.website !== '#' && p.website !== null ? `<a href="${p.website}" target="_blank" rel="noopener noreferrer" style="display:inline-flex; align-items:center; gap:6px; background:${p.color}; color:#fff; padding:6px 10px; border-radius:6px; font-weight:700; font-size:.8rem; text-decoration:none;">${tMap('partners.map.visitWebsite')}</a>` : ''}
          </div>
        `;
        marker.bindPopup(popup);

        // accumulate bounds
        if (Array.isArray(p.coordinates) && p.coordinates.length === 2) {
          allBounds.extend(p.coordinates);
        }
      });

      // Ensure all partners are visible
      if (allBounds.isValid()) {
        mapInstance.current.fitBounds(allBounds, { padding: [30, 30], maxZoom: isMobile ? 5 : 6 });
      }

      // Fix sizing after render
      setTimeout(() => {
        if (mapInstance.current) {
          mapInstance.current.invalidateSize();
        }
      }, 300);

      return () => {
        if (mapInstance.current) {
          mapInstance.current.remove();
          mapInstance.current = null;
        }
        if (mapRef.current && mapRef.current._leaflet_id) {
          try { delete mapRef.current._leaflet_id; } catch (e) { /* ignore */ }
        }
      };
    }, [isMobile, partners]);

    return (
      <>
        <section style={{ padding: isMobile ? '40px 10px' : '60px 20px', width: '100%', maxWidth: '100%', boxSizing: 'border-box', overflowX: 'hidden' }}>
          <div className="container" style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <div style={{ height: isMobile ? '420px' : '520px', width: '100%', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
              <div ref={mapRef} style={{ height: '100%', width: '100%' }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '12px', marginTop: '12px', color: '#6c757d', fontSize: '.9rem' }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ width: '10px', height: '10px', background: '#ffd700', borderRadius: '2px', display: 'inline-block' }}></span>
                {tMap('partners.map.coordinatorLabel')}
              </span>
            </div>
          </div>
        </section>

        {/* Coordinator and Beneficiaries lists - Full Width */}
        {(() => {
        if (partnersMapData.length === 0) return null;
        const coordinator = partnersMapData.find(p => p.isCoordinator);
        const beneficiaries = partnersMapData.filter(p => !p.isCoordinator);
        return (
          <>
            {/* Coordinator */}
            {coordinator && (
              <section style={{ background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)', padding: '80px 16px', width: '100%', maxWidth: '100%', boxSizing: 'border-box', overflowX: 'hidden' }}>
                <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
                  <div style={{ textAlign: 'center', marginBottom: '40px' }}>
                    <div style={{ display: 'inline-block', padding: '10px 28px', background: 'linear-gradient(135deg, #96ca2d 0%, #7aa022 100%)', borderRadius: '30px', marginBottom: '20px', boxShadow: '0 4px 15px rgba(150,202,45,0.3)' }}>
                      <span style={{ color: '#fff', fontSize: '0.85rem', fontWeight: 700, letterSpacing: '1.5px' }}>{tMap('partners.coordinator.badge')}</span>
                    </div>
                    <h2 style={{ fontSize: 'clamp(28px,4vw,40px)', color: '#2c3e50', margin: 0, fontWeight: 800, letterSpacing: '-0.5px' }}>
                      {tMap('partners.coordinator.title')}
                    </h2>
                  </div>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(300px, 1fr))',
                    gap: '24px',
                    justifyContent: 'center'
                  }}>
                    <div 
                      style={{
                        background: '#fff',
                        borderRadius: '16px',
                        padding: '24px',
                        border: '2px solid transparent',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '16px',
                        position: 'relative',
                        overflow: 'hidden'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-8px) scale(1.02)';
                        e.currentTarget.style.boxShadow = '0 12px 36px rgba(150,202,45,0.25)';
                        e.currentTarget.style.borderColor = '#96ca2d';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0) scale(1)';
                        e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.08)';
                        e.currentTarget.style.borderColor = 'transparent';
                      }}
                    >
                      {/* Decorative element */}
                      <div style={{ position: 'absolute', top: '-20px', right: '-20px', width: '80px', height: '80px', background: `radial-gradient(circle, ${coordinator.color}20 0%, transparent 70%)`, borderRadius: '50%' }}></div>
                      
                      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', position: 'relative', zIndex: 1 }}>
                        <div style={{
                          width: '64px', 
                          height: '64px', 
                          borderRadius: '14px',
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center',
                          background: coordinator.logoUrl ? 'transparent' : `linear-gradient(135deg, ${coordinator.color} 0%, ${coordinator.color}dd 100%)`,
                          fontSize: '2rem',
                          boxShadow: coordinator.logoUrl ? '0 6px 20px rgba(0,0,0,0.1)' : `0 6px 20px ${coordinator.color}30`,
                          flexShrink: 0,
                          overflow: 'hidden'
                        }}>
                          {coordinator.logoUrl ? (
                            <img 
                              src={coordinator.logoUrl} 
                              alt={coordinator.name}
                              style={{ width: '100%', height: '100%', objectFit: 'contain', padding: '4px' }}
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.parentElement.innerHTML = `<div style="width:100%;height:100%;display:flex;align-items:center;justify-content:center;font-size:2rem;background:linear-gradient(135deg, ${coordinator.color} 0%, ${coordinator.color}dd 100%);">${coordinator.logo || '🏢'}</div>`;
                              }}
                            />
                          ) : (
                            coordinator.logo || '🏢'
                          )}
                        </div>
                        <div style={{ flex: 1 }}>
                          <h4 style={{ fontSize: '1.15rem', color: '#2c3e50', margin: 0, marginBottom: '8px', fontWeight: 700, lineHeight: 1.3 }}>
                            {coordinator.fullName}
                          </h4>
                          <span style={{ 
                            display: 'inline-block', 
                            padding: '4px 10px', 
                            background: 'linear-gradient(135deg, #f0f7e6 0%, #e8f5d9 100%)', 
                            color: '#96ca2d', 
                            borderRadius: '16px', 
                            fontSize: '0.8rem', 
                            fontWeight: 600,
                            border: '1px solid rgba(150,202,45,0.2)',
                            marginBottom: '8px'
                          }}>
                            {coordinator.country}
                          </span>
                          {coordinator.pi && (
                            <p style={{ fontSize: '0.9rem', color: '#6b7280', margin: '8px 0 0 0', lineHeight: 1.5 }}>
                              <strong style={{ color: '#2c3e50' }}>PI:</strong> {coordinator.pi}
                            </p>
                          )}
                        </div>
                      </div>
                      
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 'auto', paddingTop: '12px', borderTop: '1px solid #e9ecef', position: 'relative', zIndex: 1 }}>
                        <button
                          onClick={() => setSelectedPartner(coordinator)}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            background: coordinator.color,
                            color: 'white',
                            border: 'none',
                            padding: '8px 16px',
                            borderRadius: '8px',
                            fontSize: '0.8rem',
                            fontWeight: 600,
                            cursor: 'pointer',
                            transition: 'all 0.3s ease',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateY(-2px)';
                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.25)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.15)';
                          }}
                        >
                          <i className="fas fa-eye" />
                          {t('home.consortium.viewDetails')}
                        </button>
                        {coordinator.website && coordinator.website !== '#' && coordinator.website !== null && (
                          <a
                            href={coordinator.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '6px',
                              color: '#96ca2d',
                              textDecoration: 'none',
                              fontSize: '0.9rem',
                              fontWeight: 600,
                              transition: 'all 0.3s ease'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.gap = '10px';
                              e.currentTarget.style.color = '#7aa022';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.gap = '6px';
                              e.currentTarget.style.color = '#96ca2d';
                            }}
                          >
                            {tMap('partners.map.visitWebsite')} <i className="fas fa-external-link-alt"></i>
                          </a>
                        )}
                        </div>
                    </div>
                  </div>
                </div>
              </section>
            )}

            {/* Beneficiaries */}
            <section style={{ padding: '80px 16px', width: '100%', maxWidth: '100%', boxSizing: 'border-box', overflowX: 'hidden' }}>
              <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
                <div style={{ textAlign: 'center', marginBottom: '40px' }}>
                  <div style={{ display: 'inline-block', padding: '10px 28px', background: 'linear-gradient(135deg, #96ca2d 0%, #7aa022 100%)', borderRadius: '30px', marginBottom: '20px', boxShadow: '0 4px 15px rgba(150,202,45,0.3)' }}>
                    <span style={{ color: '#fff', fontSize: '0.85rem', fontWeight: 700, letterSpacing: '1.5px' }}>{tMap('partners.beneficiaries.badge')}</span>
                  </div>
                  <h2 style={{ fontSize: 'clamp(28px,4vw,40px)', color: '#2c3e50', margin: 0, fontWeight: 800, letterSpacing: '-0.5px' }}>
                    {tMap('partners.beneficiaries.title')}
                  </h2>
                </div>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(300px, 1fr))',
                  gap: '24px',
                  justifyContent: 'center'
                }}>
                  {beneficiaries.map(b => (
                    <div 
                      key={b.id} 
                      style={{
                        background: '#fff',
                        borderRadius: '16px',
                        padding: '24px',
                        border: '2px solid transparent',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '16px',
                        position: 'relative',
                        overflow: 'hidden'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-8px) scale(1.02)';
                        e.currentTarget.style.boxShadow = '0 12px 36px rgba(150,202,45,0.25)';
                        e.currentTarget.style.borderColor = '#96ca2d';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0) scale(1)';
                        e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.08)';
                        e.currentTarget.style.borderColor = 'transparent';
                      }}
                    >
                      {/* Decorative element */}
                      <div style={{ position: 'absolute', top: '-20px', right: '-20px', width: '80px', height: '80px', background: `radial-gradient(circle, ${b.color}20 0%, transparent 70%)`, borderRadius: '50%' }}></div>
                      
                      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', position: 'relative', zIndex: 1 }}>
                        <div style={{
                          width: '64px', 
                          height: '64px', 
                          borderRadius: '14px',
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center',
                          background: b.logoUrl ? 'transparent' : `linear-gradient(135deg, ${b.color} 0%, ${b.color}dd 100%)`,
                          fontSize: '2rem',
                          boxShadow: b.logoUrl ? '0 6px 20px rgba(0,0,0,0.1)' : `0 6px 20px ${b.color}30`,
                          flexShrink: 0,
                          overflow: 'hidden'
                        }}>
                          {b.logoUrl ? (
                            <img 
                              src={b.logoUrl} 
                              alt={b.name}
                              style={{ width: '100%', height: '100%', objectFit: 'contain', padding: '4px' }}
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.parentElement.innerHTML = `<div style="width:100%;height:100%;display:flex;align-items:center;justify-content:center;font-size:2rem;background:linear-gradient(135deg, ${b.color} 0%, ${b.color}dd 100%);">${b.logo || '🏢'}</div>`;
                              }}
                            />
                          ) : (
                            b.logo || '🏢'
                          )}
                        </div>
                        <div style={{ flex: 1 }}>
                          <h4 style={{ fontSize: '1.15rem', color: '#2c3e50', margin: 0, marginBottom: '8px', fontWeight: 700, lineHeight: 1.3 }}>
                            {b.fullName || b.name}
                          </h4>
                          <span style={{ 
                            display: 'inline-block', 
                            padding: '4px 10px', 
                            background: 'linear-gradient(135deg, #f0f7e6 0%, #e8f5d9 100%)', 
                            color: '#96ca2d', 
                            borderRadius: '16px', 
                            fontSize: '0.8rem', 
                            fontWeight: 600,
                            border: '1px solid rgba(150,202,45,0.2)',
                            marginBottom: '8px'
                          }}>
                            {b.country}
                          </span>
                          {b.pi && (
                            <p style={{ fontSize: '0.9rem', color: '#6b7280', margin: '8px 0 0 0', lineHeight: 1.5 }}>
                              <strong style={{ color: '#2c3e50' }}>PI:</strong> {b.pi}
                            </p>
                          )}
                        </div>
                      </div>
                      
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 'auto', paddingTop: '12px', borderTop: '1px solid #e9ecef', position: 'relative', zIndex: 1 }}>
                        <button
                          onClick={() => setSelectedPartner(b)}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            background: b.color,
                            color: 'white',
                            border: 'none',
                            padding: '8px 16px',
                            borderRadius: '8px',
                            fontSize: '0.8rem',
                            fontWeight: 600,
                            cursor: 'pointer',
                            transition: 'all 0.3s ease',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateY(-2px)';
                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.25)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.15)';
                          }}
                        >
                          <i className="fas fa-eye" />
                          {t('home.consortium.viewDetails')}                        </button>
                        {b.website && b.website !== '#' && b.website !== null && (
                          <a
                            href={b.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '6px',
                              color: '#96ca2d',
                              textDecoration: 'none',
                              fontSize: '0.9rem',
                              fontWeight: 600,
                              transition: 'all 0.3s ease'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.gap = '10px';
                              e.currentTarget.style.color = '#7aa022';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.gap = '6px';
                              e.currentTarget.style.color = '#96ca2d';
                            }}
                          >
                            {tMap('partners.map.visitWebsite')} <i className="fas fa-external-link-alt"></i>
                          </a>
                        )}
                        </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          </>
        );
      })()}

      {/* Modal pour afficher les détails du partenaire */}
      {selectedPartner && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            padding: isMobile ? '20px' : '40px',
            overflowY: 'auto'
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setSelectedPartner(null);
            }
          }}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '16px',
              maxWidth: isMobile ? '100%' : '800px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
              position: 'relative'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{
              background: `linear-gradient(135deg, ${selectedPartner.color} 0%, ${selectedPartner.color}dd 100%)`,
              padding: '24px',
              borderRadius: '16px 16px 0 0',
              color: 'white',
              position: 'relative'
            }}>
              <button
                onClick={() => setSelectedPartner(null)}
                style={{
                  position: 'absolute',
                  top: '16px',
                  right: '16px',
                  background: 'rgba(255, 255, 255, 0.2)',
                  border: 'none',
                  borderRadius: '50%',
                  width: '36px',
                  height: '36px',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '18px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)';
                  e.currentTarget.style.transform = 'rotate(90deg)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                  e.currentTarget.style.transform = 'rotate(0deg)';
                }}
              >
                ✕
              </button>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '12px' }}>
                {selectedPartner.logoUrl ? (
                  <img
                    src={selectedPartner.logoUrl}
                    alt={selectedPartner.name}
                    style={{
                      width: '80px',
                      height: '80px',
                      objectFit: 'contain',
                      borderRadius: '12px',
                      background: 'rgba(255, 255, 255, 0.2)',
                      padding: '8px'
                    }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                ) : (
                  <div style={{
                    width: '80px',
                    height: '80px',
                    borderRadius: '12px',
                    background: 'rgba(255, 255, 255, 0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '2.5rem'
                  }}>
                    {selectedPartner.logo || '🏢'}
                  </div>
                )}
                <div style={{ flex: 1 }}>
                  <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700, marginBottom: '4px' }}>
                    {selectedPartner.name}
                  </h2>
                  <p style={{ margin: 0, fontSize: '1rem', opacity: 0.9 }}>
                    {selectedPartner.fullName}
                  </p>
                </div>
              </div>
            </div>

            {/* Content */}
            <div style={{ padding: '24px' }}>
              {/* Basic Info */}
              <div style={{ marginBottom: '24px' }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '16px' }}>
                  <span style={{
                    padding: '6px 12px',
                    background: `${selectedPartner.color}20`,
                    color: selectedPartner.color,
                    borderRadius: '20px',
                    fontSize: '0.9rem',
                    fontWeight: 600
                  }}>
                    {selectedPartner.country}
                  </span>
                  {selectedPartner.isCoordinator && (
                    <span style={{
                      padding: '6px 12px',
                      background: '#ffd700',
                      color: '#2c3e50',
                      borderRadius: '20px',
                      fontSize: '0.9rem',
                      fontWeight: 600
                    }}>
                      {tMap('partners.map.coordinator')}
                    </span>
                  )}
                </div>
                {selectedPartner.pi && (
                  <p style={{ margin: '0 0 16px 0', fontSize: '1rem', color: '#666' }}>
                    <strong style={{ color: '#2c3e50' }}>{tMap('home.consortium.principalInvestigator', 'Principal Investigator')}:</strong> {selectedPartner.pi}
                  </p>
                )}
              </div>

              {/* Work Packages Lead */}
              {selectedPartner.workPackagesLead && selectedPartner.workPackagesLead.length > 0 && (
                <div style={{ marginBottom: '24px' }}>
                  <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#2c3e50', marginBottom: '12px' }}>
                    {tMap('home.consortium.leadLabel')}
                  </h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {selectedPartner.workPackagesLead.map((wp, index) => {
                      const wpId = typeof wp === 'string' ? wp : wp.id;
                      const wpTitle = typeof wp === 'string' ? (wpMeta[wp]?.title || '') : (wp.title || wpMeta[wpId]?.title || '');
                      const wpLead = typeof wp === 'string' ? (wpMeta[wp]?.lead || '') : (wp.lead || wpMeta[wpId]?.lead || '');
                      return (
                        <div key={index} style={{
                          padding: '12px',
                          background: '#f8f9fa',
                          borderRadius: '8px',
                          borderLeft: `4px solid ${selectedPartner.color}`
                        }}>
                          <div style={{ fontWeight: 600, color: '#2c3e50', marginBottom: '4px' }}>
                            {wpId}{wpTitle ? ` — ${wpTitle}` : ''}
                          </div>
                          {wpLead && (
                            <div style={{ fontSize: '0.9rem', color: '#666', fontStyle: 'italic' }}>
                              Lead: {wpLead}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Work Packages Contributing */}
              {selectedPartner.workPackagesContributing && selectedPartner.workPackagesContributing.length > 0 && (
                <div style={{ marginBottom: '24px' }}>
                  <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#2c3e50', marginBottom: '12px' }}>
                    {tMap('home.consortium.contributingLabel')}
                  </h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {selectedPartner.workPackagesContributing.map((wp, index) => {
                      const wpId = typeof wp === 'string' ? wp : wp.id;
                      const wpTitle = typeof wp === 'string' ? (wpMeta[wp]?.title || '') : (wp.title || wpMeta[wpId]?.title || '');
                      const wpLead = typeof wp === 'string' ? (wpMeta[wp]?.lead || '') : (wp.lead || wpMeta[wpId]?.lead || '');
                      return (
                        <div key={index} style={{
                          padding: '12px',
                          background: '#fff9e6',
                          borderRadius: '8px',
                          borderLeft: `4px solid #f59e0b`
                        }}>
                          <div style={{ fontWeight: 600, color: '#2c3e50', marginBottom: '4px' }}>
                            {wpId}{wpTitle ? ` — ${wpTitle}` : ''}
                          </div>
                          {wpLead && (
                            <div style={{ fontSize: '0.9rem', color: '#666', fontStyle: 'italic' }}>
                              Lead: {wpLead}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Expertise */}
              {selectedPartner.expertise && (
                <div style={{ marginBottom: '24px' }}>
                  <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#2c3e50', marginBottom: '12px' }}>
                    {tMap('partners.expertise', 'Expertise')}
                  </h3>
                  <p style={{ margin: 0, fontSize: '1rem', color: '#666', lineHeight: 1.6 }}>
                    {selectedPartner.expertise}
                  </p>
                </div>
              )}

              {/* Website */}
              {selectedPartner.website && selectedPartner.website !== '#' && selectedPartner.website !== null && (
                <div style={{ marginTop: '24px', paddingTop: '24px', borderTop: '1px solid #e9ecef' }}>
                  <a
                    href={selectedPartner.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      background: selectedPartner.color,
                      color: 'white',
                      padding: '12px 24px',
                      borderRadius: '8px',
                      textDecoration: 'none',
                      fontSize: '1rem',
                      fontWeight: 600,
                      transition: 'all 0.3s ease',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 6px 16px rgba(0,0,0,0.25)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                    }}
                  >
                    {tMap('partners.map.visitWebsite')} <i className="fas fa-external-link-alt"></i>
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      </>
    );
  };


  return (
    <>
         <div className="home page-template page-template-page-templates page-template-fullwidth-content page-template-page-templatesfullwidth-content-php page page-id-4059 jet-theme-core jet-theme-core--has-template jet-theme-core--has-header jet-theme-core--has-body jet-theme-core--has-footer layout-fullwidth blog-masonry elementor-default elementor-kit-5747 elementor-page elementor-page-4059"><div id="jet-theme-core-document" className="jet-theme-core-document jet-theme-core-document--elementor-content-type"><div className="jet-theme-core-document__inner">
	
	<Navbar/>
	
	{/* Composant de test des icônes FontAwesome */}
	
	<article id="post-4059" className="post-4059 page type-page status-publish hentry">	
			<div data-elementor-type="wp-page" data-elementor-id="4059" className="elementor elementor-4059">
				<section className="elementor-section elementor-top-section elementor-element elementor-element-ic2p76x elementor-section-full_width elementor-section-height-default elementor-section-height-default" data-id="ic2p76x" data-element_type="section" data-settings="{&quot;jet_parallax_layout_list&quot;:[{&quot;jet_parallax_layout_image&quot;:{&quot;url&quot;:&quot;&quot;,&quot;id&quot;:&quot;&quot;,&quot;size&quot;:&quot;&quot;},&quot;_id&quot;:&quot;5759a04&quot;,&quot;jet_parallax_layout_image_tablet&quot;:{&quot;url&quot;:&quot;&quot;,&quot;id&quot;:&quot;&quot;,&quot;size&quot;:&quot;&quot;},&quot;jet_parallax_layout_image_mobile&quot;:{&quot;url&quot;:&quot;&quot;,&quot;id&quot;:&quot;&quot;,&quot;size&quot;:&quot;&quot;},&quot;jet_parallax_layout_speed&quot;:{&quot;unit&quot;:&quot;%&quot;,&quot;size&quot;:50,&quot;sizes&quot;:[]},&quot;jet_parallax_layout_type&quot;:&quot;scroll&quot;,&quot;jet_parallax_layout_direction&quot;:null,&quot;jet_parallax_layout_fx_direction&quot;:null,&quot;jet_parallax_layout_z_index&quot;:&quot;&quot;,&quot;jet_parallax_layout_bg_x&quot;:50,&quot;jet_parallax_layout_bg_x_tablet&quot;:&quot;&quot;,&quot;jet_parallax_layout_bg_x_mobile&quot;:&quot;&quot;,&quot;jet_parallax_layout_bg_y&quot;:50,&quot;jet_parallax_layout_bg_y_tablet&quot;:&quot;&quot;,&quot;jet_parallax_layout_bg_y_mobile&quot;:&quot;&quot;,&quot;jet_parallax_layout_bg_size&quot;:&quot;auto&quot;,&quot;jet_parallax_layout_bg_size_tablet&quot;:&quot;&quot;,&quot;jet_parallax_layout_bg_size_mobile&quot;:&quot;&quot;,&quot;jet_parallax_layout_animation_prop&quot;:&quot;transform&quot;,&quot;jet_parallax_layout_on&quot;:[&quot;desktop&quot;,&quot;tablet&quot;]}]}">
					<div className="elementor-container elementor-column-gap-default">
						<div className="elementor-column elementor-col-100 elementor-top-column elementor-element elementor-element-im9c73l" data-id="im9c73l" data-element_type="column">
							<div className="elementor-widget-wrap elementor-element-populated">
								<div className="elementor-element elementor-element-0bd4fc3 elementor-widget elementor-widget-jet-slider" data-id="0bd4fc3" data-element_type="widget" data-settings="{&quot;slider_height&quot;:{&quot;unit&quot;:&quot;px&quot;,&quot;size&quot;:852,&quot;sizes&quot;:[]},&quot;_animation&quot;:&quot;none&quot;,&quot;slider_height_tablet&quot;:{&quot;unit&quot;:&quot;px&quot;,&quot;size&quot;:650,&quot;sizes&quot;:[]},&quot;slider_height_mobile&quot;:{&quot;unit&quot;:&quot;px&quot;,&quot;size&quot;:500,&quot;sizes&quot;:[]}}" data-widget_type="jet-slider.default">
									<div className="elementor-widget-container">
										<div className="elementor-jet-slider jet-elements">
											<div style={{ position: 'relative', width: '100%', height: '400px', overflow: 'hidden' }}>
                                <img 
                                                    src={partenaireImg}
                                                    alt="Partners hero"
                                                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} 
                                                />
												<div style={{ position: 'absolute', inset: 0, background: 'rgba(0, 0, 0, 0.45)' }} />
												<div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
                                                  <div style={{ background: 'rgba(0,0,0,0.45)', padding: '16px 24px', borderRadius: '12px', boxShadow: '0 10px 30px rgba(0,0,0,0.25)', backdropFilter: 'blur(2px)', maxWidth: '960px', width: '100%' }}>
                                                        <h1 style={{ color: '#fff', fontSize: 'clamp(28px,5vw,46px)', fontWeight: 800, margin: 0, textAlign: 'center', letterSpacing: '0.5px' }}>{t('partners.hero.title')}</h1>
                                                        <p style={{ color: 'rgba(255,255,255,0.95)', fontSize: 'clamp(14px,2.5vw,18px)', lineHeight: 1.6, margin: '12px auto 0', textAlign: 'center', maxWidth: '800px' }}>
                                                          {t('partners.hero.description')}
                                                        </p>
                                                        

                                                     
														
                </div>
              </div>
            </div>
                  </div>
                </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
            </div>
        </article>

          {loading ? (
            <div style={{ textAlign: 'center', padding: '60px 20px' }}>
              <div style={{ fontSize: '24px', color: '#96ca2d', marginBottom: '20px' }}>
                <i className="fas fa-spinner fa-spin" />
              </div>
              <p style={{ fontSize: '16px', color: '#666' }}>{t('partners.loading', 'Loading partners...')}</p>
            </div>
          ) : error ? (
            <div style={{ textAlign: 'center', padding: '60px 20px' }}>
              <div style={{ fontSize: '24px', color: '#ef4444', marginBottom: '20px' }}>
                <i className="fas fa-exclamation-triangle" />
              </div>
              <p style={{ fontSize: '16px', color: '#666', marginBottom: '20px' }}>{error}</p>
            </div>
          ) : (
          <PartnersMap />
          )}
      <Footer />
        </div></div></div>
    </>
  );
};

export default Partners;
